(ns leg.api
  "Where the server is, and the one way to ask it something. EDN in, EDN
  out - both ends are Clojure, so a keyword that goes out comes back a
  keyword and there is nothing to translate."
  (:require [clojure.string :as str]
            [goog.object :as gobj]
            [kc.core :as kc]
            [leg.auth :as auth]))

(def dev-spa-port
  "shadow-cljs's own http server (see shadow-cljs.edn). A page loaded from
  it is a page being worked on, and the server it belongs to is the one
  next door."
  "50021")

(def dev-server-port "50020")

(defn base []
  (or (gobj/get js/globalThis "LESER_API")
      (when (= dev-spa-port (.-port js/location))
        (str (.-protocol js/location) "//" (.-hostname js/location) ":" dev-server-port))
      ""))

(defn- with-key
  "Every call carries this window's key. A request that is refused because
  there is none is not the same as a server that is not there, so the 401
  is turned into something the caller can tell apart."
  [opts]
  (let [o (or opts #js {})
        hs (js/Object.assign #js {} (or (gobj/get o "headers") #js {})
                             (clj->js (or (auth/headers) {})))]
    (gobj/set o "headers" hs)
    o))

(defn- fail-with-message
  "The server says what was wrong with a request, in EDN. Reading it is
  worth the round trip: \"this address does not answer with a feed\" is an
  answer, and \"422\" is not."
  [^js res]
  (-> (.text res)
      (.then (fn [body]
               (let [m (try (kc/read-edn body) (catch :default _ nil))]
                 (throw (ex-info (or (:message m)
                                     (str "the server answered " (.-status res)))
                                 {:type (or (:error m) :server)})))))))

(defn request [path opts]
  (-> (js/fetch (str (base) path) (with-key opts))
      (.then (fn [^js res]
               (cond
                 (.-ok res)             (.text res)
                 (= 401 (.-status res)) (throw (ex-info "unauthorized" {:type :unauthorized}))
                 :else                  (fail-with-message res))))
      (.then kc/read-edn)
      (.then (fn [answer] (auth/ok!) answer))
      (.catch (fn [e]
                (when (= :unauthorized (:type (ex-data e))) (auth/needed!))
                (throw e)))))

(defn unauthorized? [e] (= :unauthorized (:type (ex-data e))))

(defn message-of
  "What to put in front of somebody when a call failed. The server's own
  words where there are any - it knows what was wrong with the request and
  the window does not."
  [e]
  (or (kc/str-trimmed-non-blank (ex-message e))
      "The server could not be reached."))

(defn- post [path body]
  (request path (clj->js (cond-> {:method "POST"}
                           body (assoc :headers {"content-type" "application/edn"}
                                       :body (kc/->edn body))))))

;;; ------------------------------------------------------------------ auth

(defn request-code!
  "Ask for a code at an address. An address nobody here has used makes a
  reader on the server - there is no sign-up call, because there is no
  sign-up."
  [email]
  (post "/api/auth/request" {:email email}))

(defn verify-code!
  "The code for a key. Stores the key and takes the flag down - the caller
  has to do neither."
  [email code]
  (-> (post "/api/auth/verify" {:email email :code code})
      (.then (fn [answer]
               (auth/save-token! (:token answer))
               (auth/satisfied! (select-keys answer [:client :name]))
               answer))))

;;; ----------------------------------------------------------------- state

(defn state! [] (request "/api/state" nil))

;;; ----------------------------------------------------------------- feeds

(defn add-feed! [url] (post "/api/feeds" {:url url}))
(defn update-feed! [id m] (post (str "/api/feeds/" id) m))
(defn refresh-feed! [id] (post (str "/api/feeds/" id "/refresh") nil))
(defn read-feed!
  "Everything in one feed read, or only what appeared more than
  `older-than` seconds ago. An age and not a cutoff instant: the clock that
  dated these articles is the server's, and a phone whose own is a minute
  out would otherwise mark a minute more than the button it was pressed on
  said."
  ([id] (read-feed! id nil))
  ([id older-than]
   (post (str "/api/feeds/" id "/read")
         (when older-than {:older-than older-than}))))
(defn refresh-all! [] (post "/api/refresh" nil))
(defn import-opml! [text] (post "/api/import" {:text text}))
(defn export-opml! [] (request "/api/export" nil))

(defn remove-feed! [id]
  (request (str "/api/feeds/" id) #js {:method "DELETE"}))

(defn remove-all-feeds!
  "The collection instead of one member of it - the same verb, without an
  id."
  []
  (request "/api/feeds" #js {:method "DELETE"}))

;;; -------------------------------------------------------------- articles

(defn articles!
  "A feed's list, a group's, or everything. Both names go out escaped, and a
  group's needs it: it is a word somebody typed."
  [{:keys [feed group unread? starred? limit]}]
  (let [q (cond-> []
            (kc/str-trimmed-non-blank feed)  (conj (str "feed=" (js/encodeURIComponent feed)))
            (kc/str-trimmed-non-blank group) (conj (str "group=" (js/encodeURIComponent group)))
            unread?                          (conj "unread=1")
            starred?                         (conj "starred=1")
            limit                            (conj (str "limit=" limit)))]
    (request (str "/api/articles" (when (seq q) (str "?" (str/join "&" q)))) nil)))

(defn article! [id]
  (request (str "/api/article?id=" (js/encodeURIComponent id)) nil))

(defn mark-read! [ids read?] (post "/api/articles/read" {:ids (vec ids) :read? read?}))
(defn star! [ids starred?] (post "/api/articles/star" {:ids (vec ids) :starred? starred?}))

;;; ----------------------------------------------------------------- admin

(defn admin-readers!
  "Everybody who reads on this server. Refused with a 403 for anybody but
  the one address the server has in its code."
  []
  (request "/api/admin/readers" nil))

(defn add-reader!
  "Make somebody an account before they ask for one. Answers the list again,
  and whether that address was new."
  [email]
  (post "/api/admin/readers" {:email email}))

(defn set-door!
  "Hold the door open for the next new address, or shut it again. Answers
  the list and the door as they now are."
  [open?]
  (post "/api/admin/door" {:open? open?}))

;;; -------------------------------------------------------------- settings

(defn save-settings! [m] (post "/api/settings" m))
(defn remove-client! [id] (request (str "/api/clients/" (name id)) #js {:method "DELETE"}))
